import { useState, FC } from 'react';
import { Icon, IconProps } from '@kizen/kds/Icon';
import { Tooltip, useTooltip } from '@kizen/kds/Tooltip';

interface InteractiveIconButtonProps {
  icon: string;
  tooltip: string;
  tooltipPosition?: 'top' | 'bottom' | 'left' | 'right';
  onActive: () => void;
  isActive?: boolean;
  isDisabled?: boolean;
  persistActiveState?: boolean;
}

export const InteractiveIconButton: FC<InteractiveIconButtonProps> = ({
  icon,
  tooltip,
  tooltipPosition = 'bottom',
  onActive,
  isActive = false,
  isDisabled = false,
  persistActiveState = false,
}) => {
  const [isClicked, setIsClicked] = useState(isActive);
  const { triggerProps, targetProps, showTooltip, tooltipProps } =
    useTooltip(true);

  const handleClick = () => {
    if (isDisabled) return;

    if (persistActiveState) {
      setIsClicked((prev) => {
        if (prev === true) {
          return false;
        } else {
          onActive();
          return true;
        }
      });
    } else {
      onActive();
    }
  };

  const displayActiveState = persistActiveState ? isClicked : isActive;

  return (
    <div
      className={`flex items-center hover:text-gray-900 cursor-pointer ${
        displayActiveState ? 'text-gray-900' : 'text-font-secondary'
      } ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
      onClick={handleClick}
    >
      <span
        {...triggerProps}
        {...targetProps}
        data-qa="interactive-icon-button"
      >
        <Icon size="sm" icon={icon as IconProps['icon']} />
        {showTooltip ? (
          <Tooltip
            position={tooltipPosition}
            text={tooltip}
            {...tooltipProps}
          />
        ) : null}
      </span>
    </div>
  );
};
